import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import classnames from 'classnames';
import ActionableIcon, { IActionableIconProps } from '../ActionableIcon/ActionableIcon';
import AnchorOrButtonMenuChoice, {
  TMenuChoice,
} from '../AnchorOrButtonMenuChoice/AnchorOrButtonMenuChoice';
import useStyles from './ActionableMenuIcon.styles';

export interface IActionableMenuIconProps
  extends Omit<IActionableIconProps, 'onClick' | 'menuChoices'> {
  /**
   * List of actions available in the icon menu. Can be a button(default) or anchor element.
   * It will be an anchor if a href is passed.
   */
  menuChoices: TMenuChoice[];
  /** The minimum display width for the action menu */
  minWidth?: string | number;
  /** Whether to align to the action menu with the right or left edge of the actionable icon */
  iconMenuAlignment?: 'left' | 'right';
  /** Optional JSS class for further styling */
  className?: string;
}

const ActionableMenuIcon = ({
  children,
  className,
  iconMenuAlignment = 'right',
  menuChoices,
  minWidth,
  ...actionableIconProps
}: IActionableMenuIconProps) => {
  const classes = useStyles(useMemo(() => ({ minWidth }), [minWidth]));
  const component = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (component.current && !component.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [component]);

  const onClickIcon = useCallback(() => {
    setOpen(!isOpen);
  }, [isOpen]);

  const onClickMenuChoice = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <div ref={component} className={`${classes.root} ${className}`}>
      <ActionableIcon
        className={isOpen ? classes.openMenuIcon : undefined}
        onClick={onClickIcon}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...actionableIconProps}
      >
        {children}
      </ActionableIcon>
      {isOpen && (
        <div
          className={classnames(classes.menu, {
            [classes.left]: iconMenuAlignment === 'left',
            [classes.right]: iconMenuAlignment === 'right',
          })}
        >
          {menuChoices.map((menuChoice) => {
            return (
              <AnchorOrButtonMenuChoice
                key={menuChoice.label}
                onClickMenuChoice={onClickMenuChoice}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...menuChoice}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionableMenuIcon;
